import { JourneyFrame, Note, SourceChip } from './JourneyChrome'
import { type SampleMaterial, type SelfReport, type Topic } from './model'
import { DEMO_TEXT, PANEL, PRIMARY } from './ui'

/**
 * Step 4: a short explanation of the chosen topic — the key idea, the rule in
 * a few points, one worked example and the common mistake — then practice.
 * "Con hiểu chưa?" is the learner's self-report (Curriculum and Source-Based
 * Learning §4.5): it can show a second, simpler explanation, and it is never
 * treated as evidence of understanding. Once the topic is chosen the learner
 * stays with the explanation and practice: no link back to the material
 * (User Features UP-05, UP-09).
 */

const SELF_REPORT: readonly { id: SelfReport; label: string; icon: string }[] = [
  { id: 'understood', label: 'Con hiểu rồi', icon: '😀' },
  { id: 'partly', label: 'Hiểu một phần', icon: '🙂' },
  { id: 'again', label: 'Giải thích lại', icon: '🤔' },
]

export function LearnStep({
  sample,
  topic,
  selfReport,
  onSelfReport,
  onPractice,
  onBack,
  onHome,
}: {
  sample: SampleMaterial
  topic: Topic
  selfReport: SelfReport | null
  onSelfReport: (report: SelfReport) => void
  onPractice: () => void
  onBack: () => void
  onHome: () => void
}): React.JSX.Element {
  const { explanation } = topic
  const lang = sample.subject === 'english' ? 'en' : undefined

  return (
    <JourneyFrame step="Học" title={topic.title} onBack={onBack} onHome={onHome} demo>
      <SourceChip sample={sample} />

      <p className="rounded-2xl bg-sky-50 px-4 py-3 text-lg dark:bg-sky-950/50">
        <span className="font-semibold">🎯 Mục tiêu: </span>
        {topic.objective}
      </p>

      <section
        aria-labelledby="key-idea"
        className="flex flex-col gap-2 rounded-3xl border-2 border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40"
      >
        <h2
          id="key-idea"
          className="text-base font-semibold text-amber-900 dark:text-amber-200"
        >
          <span aria-hidden>💡 </span>Điều quan trọng nhất
        </h2>
        <p className="text-xl font-bold">{explanation.keyIdea}</p>
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <section aria-labelledby="how-to" className={PANEL}>
          <h2 id="how-to" className="text-lg font-semibold">
            Cách làm
          </h2>
          <ul className="flex flex-col gap-2">
            {explanation.points.map((point) => (
              <li key={point} className="flex gap-2 text-lg">
                <span aria-hidden className="text-sky-700 dark:text-sky-300">
                  ●
                </span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="worked-example" className={PANEL}>
          <h2 id="worked-example" className="text-lg font-semibold">
            Ví dụ
          </h2>
          <p
            lang={lang}
            className="rounded-xl bg-slate-100 px-3 py-2 text-xl font-semibold dark:bg-slate-800"
          >
            {explanation.example.problem}
          </p>
          <ol className="flex flex-col gap-1">
            {explanation.example.steps.map((s, i) => (
              <li key={s} className="flex gap-2 text-lg">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-sky-100 text-base font-semibold text-sky-900 dark:bg-sky-900 dark:text-sky-100">
                  {i + 1}
                </span>
                <span lang={lang}>{s}</span>
              </li>
            ))}
          </ol>
          <p
            lang={lang}
            className="text-lg font-semibold text-emerald-800 dark:text-emerald-300"
          >
            → {explanation.example.answer}
          </p>
        </section>
      </div>

      <p className="rounded-2xl bg-rose-50 px-4 py-3 text-lg dark:bg-rose-950/40">
        <span className="font-semibold">⚠️ Cẩn thận: </span>
        {explanation.commonMistake}
      </p>

      <Note>{DEMO_TEXT.explanation}</Note>

      <section aria-labelledby="self-check" className="flex flex-col gap-3">
        <h2 id="self-check" className="text-lg font-semibold">
          Con hiểu chưa?
        </h2>
        <div className="grid grid-cols-3 gap-2">
          {SELF_REPORT.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={selfReport === option.id}
              className="flex min-h-16 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-slate-300 bg-white p-2 text-base font-semibold aria-pressed:border-sky-700 aria-pressed:bg-sky-50 dark:border-slate-700 dark:bg-slate-900 dark:aria-pressed:border-sky-400 dark:aria-pressed:bg-sky-950"
              onClick={() => {
                onSelfReport(option.id)
              }}
            >
              <span aria-hidden className="text-2xl">
                {option.icon}
              </span>
              {option.label}
            </button>
          ))}
        </div>
        {selfReport === 'again' && (
          <section
            aria-label="Giải thích cách khác"
            className="rounded-2xl bg-emerald-50 px-4 py-3 text-lg dark:bg-emerald-950/40"
          >
            <p className="font-semibold">Thử hiểu theo cách này nhé:</p>
            <p>{explanation.explainAgain}</p>
          </section>
        )}
      </section>

      <div className="flex flex-col gap-3 md:flex-row">
        <button type="button" className={PRIMARY} onClick={onPractice}>
          Luyện tập ({topic.questions.length} câu)
        </button>
      </div>
    </JourneyFrame>
  )
}
